import { useState } from "react";
import { AlertTriangle, Check, X } from "lucide-react";

import type { AssistantRevalidation, ProposalDetail } from "../../api/types";
import { useDecideProposal } from "../../api/queries";
import { StatusBadge, type BadgeTone } from "../../components/StatusBadge";

/**
 * Le geste proposé, avant qu'il n'existe (V1.6-12.2).
 *
 * **Rien n'a eu lieu tant que personne n'a cliqué.** La carte montre ce qui va
 * se passer — quel geste, sur quoi, avec quels chiffres, et sur quels faits
 * l'assistant s'appuyait — parce qu'un bouton « confirmer » sans ces réponses
 * demande de signer à l'aveugle.
 *
 * Après le clic, elle dit ce qui s'est réellement produit : exécuté, ou refusé
 * parce que les données ont changé, avec le chiffre d'aujourd'hui. « Impossible »
 * tout court ne laisse rien faire à celui qui le lit.
 */
export function ProposalCard({
  detail,
  conversationId,
}: {
  detail: ProposalDetail;
  conversationId: string;
}) {
  const { proposal, decision } = detail;
  const decide = useDecideProposal(conversationId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const settled = decision !== null;
  const result = decide.data?.revalidation ?? null;

  function confirm() {
    decide.mutate({ proposalId: proposal.id, payload: { outcome: "ACCEPTED" } });
  }

  function reject() {
    decide.mutate({ proposalId: proposal.id, payload: { outcome: "REJECTED" } });
  }

  function applyChange() {
    decide.mutate({
      proposalId: proposal.id,
      payload: { outcome: "MODIFIED", modifications: draft },
    });
    setEditing(false);
  }

  return (
    <section
      aria-label="Geste proposé"
      className="mt-1 flex flex-col gap-2 rounded-md border border-border bg-surface px-3 py-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wide text-ink-subtle">
          {ACTION_LABELS[proposal.action_type]}
        </span>
        {settled ? (
          <StatusBadge tone={OUTCOME_TONE[decision.execution_state]}>
            {EXECUTION_LABELS[decision.execution_state]}
          </StatusBadge>
        ) : (
          <StatusBadge tone="info">à confirmer</StatusBadge>
        )}
      </div>

      <p className="text-[14px] text-ink">{proposal.summary}</p>

      {/* Ce que l'assistant lisait. Relu au moment de confirmer, et c'est
          pour ça qu'on le montre. */}
      {Object.keys(proposal.fact_snapshot).length > 0 && (
        <details>
          <summary className="cursor-pointer text-[11px] text-ink-subtle">
            Sur quels chiffres
          </summary>
          <dl className="flex flex-col gap-0.5 pt-1">
            {Object.entries(proposal.fact_snapshot).map(([key, value]) => (
              <div key={key} className="flex gap-2 text-[11px] text-ink-subtle">
                <dt>{key}</dt>
                <dd className="tnum">{renderFact(value)}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      {!settled && editing && (
        <div className="flex flex-col gap-1">
          {proposal.modifiable_fields.map((field) => (
            <label key={field} className="flex items-center gap-2 text-[12px] text-ink">
              <span className="text-ink-subtle">{field}</span>
              <input
                className="w-32 rounded border border-border bg-surface px-2 py-1 text-[13px]"
                defaultValue={String(proposal.proposed_arguments[field] ?? "")}
                onChange={(event) =>
                  setDraft((previous) => ({ ...previous, [field]: event.target.value }))
                }
              />
            </label>
          ))}
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded border border-brand px-2 py-1 text-[12px] text-brand"
              onClick={applyChange}
              disabled={Object.keys(draft).length === 0 || decide.isPending}
            >
              Confirmer avec ce chiffre
            </button>
            <button
              type="button"
              className="text-[12px] text-ink-subtle underline"
              onClick={() => setEditing(false)}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {!settled && !editing && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="flex items-center gap-1 rounded border border-brand bg-brand-subtle px-2 py-1 text-[12px] text-brand"
            onClick={confirm}
            disabled={decide.isPending}
          >
            <Check className="size-3" aria-hidden />
            Confirmer
          </button>
          <button
            type="button"
            className="flex items-center gap-1 rounded border border-border px-2 py-1 text-[12px] text-ink-subtle"
            onClick={reject}
            disabled={decide.isPending}
          >
            <X className="size-3" aria-hidden />
            Refuser
          </button>
          {/* « Modifier » n'existe que là où le contrat le permet. Un bouton
              qui mène à un refus est pire que pas de bouton. */}
          {proposal.modifiable_fields.length > 0 && (
            <button
              type="button"
              className="text-[12px] text-ink-subtle underline"
              onClick={() => setEditing(true)}
            >
              Modifier
            </button>
          )}
        </div>
      )}

      {decide.isPending && (
        <p className="text-[11px] text-ink-subtle">Je revérifie les chiffres…</p>
      )}

      {settled && <Aftermath detail={detail} fallback={result} />}
    </section>
  );
}

/** Ce qui s'est passé après la signature, dit avec des chiffres. */
function Aftermath({
  detail,
  fallback,
}: {
  detail: ProposalDetail;
  fallback: AssistantRevalidation | null;
}) {
  const decision = detail.decision;
  if (!decision) return null;

  if (decision.execution_state === "EXECUTED") {
    return (
      <p className="text-[11px] text-ink-subtle">
        Fait par {decision.decided_by}
        {decision.outcome === "MODIFIED" && " (avec votre chiffre)"}.
      </p>
    );
  }
  if (decision.execution_state === "NOT_ATTEMPTED") {
    return <p className="text-[11px] text-ink-subtle">Refusé. Rien n'a été fait.</p>;
  }

  const explanation = decision.error_message ?? fallback?.message ?? "";
  return (
    <p className="flex items-start gap-1 text-[11px] text-warning">
      <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
      <span>{explanation || "Les chiffres ont changé ; rien n'a été fait."}</span>
    </p>
  );
}

function renderFact(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const ACTION_LABELS: Record<string, string> = {
  SET_PROJECT_REQUIREMENT: "Liste de chantier",
  CREATE_RFQ_FROM_REQUIREMENTS: "Demande de prix",
  RESERVE_STOCK: "Réservation",
  TRANSFER_STOCK: "Transfert",
  // V1.6-13.3. Aucun champ modifiable n'apparaît pour ce geste, et ce n'est pas
  // un oubli : `modifiable_fields` est vide côté backend, donc le bloc
  // d'édition ne se rend pas. Corriger un fournisseur ou une quantité avant de
  // signer fabriquerait un plan que l'optimiseur n'a jamais produit.
  CREATE_PURCHASE_PLAN_FROM_RECOMMENDATION: "Plan d'achat",
};

const EXECUTION_LABELS: Record<string, string> = {
  NOT_ATTEMPTED: "refusé",
  EXECUTED: "fait",
  FAILED_REVALIDATION: "données changées",
  FAILED_EXECUTION: "refusé par la règle",
};

const OUTCOME_TONE: Record<string, BadgeTone> = {
  NOT_ATTEMPTED: "unknown",
  EXECUTED: "available",
  FAILED_REVALIDATION: "warning",
  FAILED_EXECUTION: "warning",
};
